/**
 * View Component Mapping
 * Maps view names to their corresponding React components
 */

import React, { Suspense, lazy } from 'react';

import { ViewErrorFallback } from '@/app/components/ViewErrorFallback';
import type { DocsOpenDocumentRequest } from '@/features/docs/utils/docsOpenRequest';
import { ErrorBoundary } from '@/shared/components';
import type { MenuItem } from '@/shared/types';
import { log } from '@/shared/utils/logger';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import i18n from '@/shared/i18n/config';

const GanttView = lazy(() =>
  import('@/features/gantt').then((m) => ({ default: m.GanttView }))
);
const KanbanView = lazy(() =>
  import('@/features/kanban/components').then((m) => ({
    default: m.KanbanView,
  }))
);
const DashboardView = lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.DashboardView }))
);
const SettingsView = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsView }))
);
const DocsView = lazy(() =>
  import('@/features/docs').then((m) => ({ default: m.DocsView }))
);
const ReportsView = lazy(() =>
  import('@/features/reports').then((m) => ({ default: m.ReportsView }))
);
const CalendarView = lazy(() =>
  import('@/features/calendar').then((m) => ({ default: m.CalendarView }))
);

interface ViewRenderOptions {
  settingsEntryTopTabHint?: 'general' | 'users' | 'projects' | 'mcp-monitoring' | 'audit-monitor' | 'guidelines' | 'feedback-mode' | 'ai' | null;
  docsOpenRequest?: Omit<DocsOpenDocumentRequest, 'requestedAt'> | null;
  onDocsOpenRequestConsumed?: () => void;
  onOpenSelfServiceTour?: (stepId?: 'dashboard' | 'gantt' | 'kanban' | 'docs' | 'reports') => void;
}

function getViewComponent(
  viewName: string,
  options: ViewRenderOptions = {},
): React.ReactNode {
  switch (viewName) {
    case 'dashboard':
      return <DashboardView onOpenSelfServiceTour={options.onOpenSelfServiceTour} />;
    case 'kanban':
      return <KanbanView />;
    case 'gantt':
      return <GanttView />;
    case 'calendar':
      return <CalendarView />;
    case 'docs':
      return (
        <DocsView
          pendingOpenRequest={options.docsOpenRequest}
          onPendingOpenRequestConsumed={options.onDocsOpenRequestConsumed}
        />
      );
    case 'reports':
      return <ReportsView />;
    case 'settings':
      return (
        <SettingsView
          entryTopTabHint={options.settingsEntryTopTabHint}
        />
      );
    default:
      return null;
  }
}

export function renderActiveView(
  activeMenuItem: MenuItem | undefined,
  theme: ThemeClasses,
  options: ViewRenderOptions = {},
): React.ReactNode {
  if (!activeMenuItem) return null;

  const component = getViewComponent(activeMenuItem.view, options);

  return (
    <ErrorBoundary
      fallback={(error, _errorInfo, reset) => (
        <ViewErrorFallback
          activeMenuItem={activeMenuItem}
          error={error}
          reset={reset}
          theme={theme}
        />
      )}
      onError={(error, errorInfo) => {
        log.error('ViewComponentMapping', `Error loading ${activeMenuItem.view} view:`, error, errorInfo);
      }}
      resetKeys={[activeMenuItem.view, activeMenuItem.label]}
    >
      <Suspense
        fallback={
          <div
            data-view-probe-state="loading"
            className={cn(
              'flex items-center justify-center h-full',
              theme.bgPrimary
            )}
          >
            <div className={cn('text-lg', theme.textSecondary)}>{i18n.t('Loading...')}</div>
          </div>
        }
      >
        {component || (
          <div
            className={cn(
              'flex items-center justify-center h-full',
              theme.bgPrimary
            )}
          >
            <div className={cn('text-lg', theme.textSecondary)}>
              {i18n.t('No component found for view:')} {activeMenuItem.view}
            </div>
          </div>
        )}
      </Suspense>
    </ErrorBoundary>
  );
}
